// src/store/authStore.js
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import {
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  onAuthStateChanged,
  signOut,
  getIdToken
} from 'firebase/auth'
import { doc, getDoc } from 'firebase/firestore'
import { auth, db } from '../firebase'
import { useRouter } from 'vue-router'
import { auditLogger } from '@/utils/auditLogger'
import { usageAnalytics } from '@/utils/usageAnalytics'
import { setMonitoringUser } from '@/utils/monitoring'
import { ROLE_MIGRATION_MAP } from '@/config/roles'
import {
  getSafeRedirectPath,
  stashPostLoginRedirect,
  takePostLoginRedirect,
  loginHrefForCurrentLocation,
  REDIRECT_QUERY_PARAM
} from '@/utils/schoolRoutes'
import { APP_BRAND } from '@/config/appBrand'
import { logSignInAttemptForUser } from '@/utils/logSignInAttempt'
import { ALL_SCHOOLS_CM_ROLES } from '@/utils/userSchoolLink'

// Normalize legacy role names to their canonical equivalents (e.g.
// administrator_504_CM → admin_504) so the rest of the frontend only ever
// deals with one role per concept, even before the backend migration is run.
const normalizeUserData = (data = {}) => {
  if (data && data.role && ROLE_MIGRATION_MAP[data.role]) {
    return { ...data, role: ROLE_MIGRATION_MAP[data.role] }
  }
  return data
}

// Set when this tab starts a full-page Google redirect sign-in, so that on
// return we can tell a genuinely failed redirect (flag present but no signed-in
// user — e.g. the browser dropped the OAuth state) apart from an ordinary app
// load where getRedirectResult() is expected to come back empty.
const REDIRECT_PENDING_KEY = 'cmv_redirect_sign_in_pending'

const setRedirectPendingFlag = () => {
  try {
    sessionStorage.setItem(REDIRECT_PENDING_KEY, '1')
  } catch {
    /* sessionStorage unavailable */
  }
}

// Returns whether the flag was set, and clears it either way.
const consumeRedirectPendingFlag = () => {
  try {
    const wasPending = sessionStorage.getItem(REDIRECT_PENDING_KEY) === '1'
    sessionStorage.removeItem(REDIRECT_PENDING_KEY)
    return wasPending
  } catch {
    return false
  }
}

// Phones and iPads use a full-page Google sign-in. A popup on those devices
// becomes a second tab that does not close after the account is chosen, so
// the person has to find their way back. Desktop keeps the popup, which does
// close itself.
export function prefersRedirectSignIn(nav) {
  const agent = nav || (typeof navigator !== 'undefined' ? navigator : null)
  if (!agent) return false
  const ua = agent.userAgent || ''
  if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile/i.test(ua)) {
    return true
  }
  return agent.platform === 'MacIntel' && agent.maxTouchPoints > 1
}

// The full-page trip only keeps the session when sign-in finishes on this
// site (hosted dev: authDomain casemangervue.web.app). On projects whose
// authDomain is still the firebaseapp.com address (Vacaville today), the
// trip crosses sites and phone browsers drop the sign-in state — the exact
// pre-July failure. Those keep the popup until their authDomain is moved.
export function redirectSignInStaysOnSite(authDomain, hostname) {
  return !!authDomain && authDomain === hostname
}

const REDIRECT_LOST_STATE_MESSAGE =
  'Sign-in did not complete because your browser lost the sign-in data during the ' +
  'redirect. Try again in Safari or Chrome directly (not a link opened inside another ' +
  'app), and avoid Private Browsing. If it keeps happening, clear this site\u2019s data ' +
  'and retry.'

export const useAuthStore = defineStore('auth', () => {
  const currentUser = ref(null)
  const errorMsg = ref('')
  const isLoading = ref(true)
  // True only when a new token was required (fresh sign-in, or a role/school
  // change) and Google did not issue it. The session stays; student data can
  // be denied until a reload succeeds. Cleared when a later restore gets a
  // matching token.
  const accessTokenStale = ref(false)
  const router = useRouter()

  // Where to land after a successful sign-in: the ?redirect= query on /login
  // is the current intent; sessionStorage is the backup when a Google
  // full-page redirect drops that query. Default home (`/`) otherwise.
  const resolvePostLoginTarget = () => {
    const fromQuery = getSafeRedirectPath(router.currentRoute.value?.query?.[REDIRECT_QUERY_PARAM])
    const fromStore = takePostLoginRedirect()
    return fromQuery || fromStore || '/'
  }

  // Cached ID tokens already carry the role claim after the first sign-in, so
  // most loads need no network call. A refresh is forced only when the claims
  // disagree with the users doc (see claimsMatchUserDoc below).
  const readTokenClaims = async (firebaseUser) => {
    if (typeof firebaseUser.getIdTokenResult !== 'function') return {}
    try {
      const result = await firebaseUser.getIdTokenResult(false)
      return result?.claims || {}
    } catch (error) {
      console.warn('Could not read the cached sign-in token:', error)
      return {}
    }
  }

  const sameIdList = (a, b) => {
    const listA = Array.isArray(a) ? [...a].sort() : []
    const listB = Array.isArray(b) ? [...b].sort() : []
    return listA.length === listB.length && listA.every((id, i) => id === listB[i])
  }

  // Whether the token claims mirror the users/{uid} doc the way syncUserClaims
  // writes them. Compared against the RAW doc (pre role-migration) because the
  // claim is a verbatim copy — normalizing first would report a mismatch on
  // every load for legacy roles and force a refresh that changes nothing.
  const claimsMatchUserDoc = (claims, docData) => {
    if (!docData?.role) return true // no role to mirror; the guard handles it
    if (claims.role !== docData.role) return false
    if (!sameIdList(claims.schoolIds, docData.schoolIds)) return false
    // allSchools is only minted for case-manager roles (see syncUserClaims) —
    // only expect it when the doc role is eligible, or a stale doc flag would
    // force a pointless refresh on every load.
    const expectAllSchools =
      docData.allSchools === true && ALL_SCHOOLS_CM_ROLES.includes(docData.role)
    if ((claims.allSchools === true) !== expectAllSchools) return false
    const expectSchoolSystem =
      docData.schoolSystemSettings === true && docData.role === 'school_admin'
    return (claims.schoolSystemSettings === true) === expectSchoolSystem
  }

  const publishSignedInUser = (firebaseUser, profile) => {
    currentUser.value = {
      uid: firebaseUser.uid,
      email: firebaseUser.email,
      name: firebaseUser.displayName,
      ...profile
    }
    setMonitoringUser(currentUser.value)
    setupTokenRefresh()
    usageAnalytics.trackSessionStart(firebaseUser.uid)
    void logSignInAttemptForUser(firebaseUser, 'admitted')
  }

  const loadUserProfile = async (firebaseUser) => {
    const userRef = doc(db, 'users', firebaseUser.uid)
    try {
      return await getDoc(userRef)
    } catch (error) {
      console.warn('Profile read failed; retrying once:', error)
      return getDoc(userRef)
    }
  }

  // Initialize auth state listener
  const initAuth = () => {
    // Popup is the normal sign-in. A blocked popup falls back to
    // signInWithRedirect, which must call getRedirectResult() on the way back.
    // A normal refresh has no redirect in progress. Calling getRedirectResult()
    // anyway makes Firebase look for redirect state that is not there, and that
    // check can drop the saved session — refresh then looks like a logout.
    let redirectPending = false
    try {
      redirectPending = sessionStorage.getItem(REDIRECT_PENDING_KEY) === '1'
    } catch {
      /* sessionStorage unavailable — treat as a normal refresh */
    }
    if (redirectPending) {
      getRedirectResult(auth)
        .then(async (result) => {
          if (!result || !result.user) {
            // This tab started a redirect and no user came back. The browser lost
            // the OAuth state mid-flight (storage partitioning, in-app browsers,
            // Private Browsing) — tell the user instead of silently bouncing
            // them back to the login screen.
            if (consumeRedirectPendingFlag() && !auth.currentUser) {
              console.warn('⚠️ Redirect sign-in returned no result after a pending redirect')
              errorMsg.value = REDIRECT_LOST_STATE_MESSAGE
            }
            return
          }
          consumeRedirectPendingFlag()
          const ok = await finalizeSignedInUser(result.user)
          if (ok && typeof window !== 'undefined' && window.location.pathname === '/login') {
            router.push(resolvePostLoginTarget())
          }
        })
        .catch((error) => {
          // Firebase throws "missing initial state" when there is no pending
          // redirect state in sessionStorage. After a hard reload or Demo Role
          // reload that is harmless noise — but right after WE started a redirect
          // sign-in it means the browser dropped the OAuth state and the user
          // needs to know why they are back on the login screen.
          const msg = String(error?.message || '')
          if (msg.includes('missing initial state')) {
            if (consumeRedirectPendingFlag() && !auth.currentUser) {
              console.warn('⚠️ Redirect sign-in state was lost by the browser:', msg)
              errorMsg.value = REDIRECT_LOST_STATE_MESSAGE
            } else {
              console.warn('Ignoring redirect-result noise:', msg)
            }
            return
          }
          consumeRedirectPendingFlag()
          console.error('❌ Redirect sign-in error:', error)
          errorMsg.value = error.message || 'Authentication failed.'
        })
    }

    onAuthStateChanged(auth, async (firebaseUser) => {
      isLoading.value = true

      try {
        if (!firebaseUser) {
          const hadSession = !!currentUser.value
          currentUser.value = null
          setMonitoringUser(null)
          accessTokenStale.value = false

          // Clear token refresh when user logs out
          clearTokenRefresh()

          // Session lost while already signed in (expired token, signed out in
          // another tab): send them to login but keep the page they were on so a
          // deep link (feedback form, etc.) survives the round-trip.
          //
          // Do NOT hard-redirect on the initial anonymous load. That used to
          // replace `/feedback/…` with bare `/login`, so after Google sign-in
          // teachers landed on the students page instead of the form they opened.
          // The router guard already sends unauthenticated visitors to
          // /login?redirect=<original>.
          if (
            hadSession &&
            typeof window !== 'undefined' &&
            window.location.pathname !== '/login'
          ) {
            window.location.assign(loginHrefForCurrentLocation())
          }
          return
        }

        const claims = await readTokenClaims(firebaseUser)

        let userDoc = null
        try {
          userDoc = await loadUserProfile(firebaseUser)
        } catch (error) {
          console.error('❌ Profile read failed:', error)
          // A failed read is not a logout. Keep the session when the token
          // already says who this user is. (Pending and unregistered users
          // never have a role claim — syncUserClaims clears theirs — so this
          // cannot let them in.)
          if (claims?.role) {
            publishSignedInUser(firebaseUser, {
              role: claims.role,
              ...(Array.isArray(claims.schoolIds) ? { schoolIds: claims.schoolIds } : {}),
              ...(claims.allSchools === true ? { allSchools: true } : {})
            })
          }
          return
        }

        if (!userDoc.exists()) {
          await logSignInAttemptForUser(firebaseUser, 'unregistered')
          currentUser.value = null
          setMonitoringUser(null)
          await signOut(auth)
          return
        }

        const rawData = userDoc.data()
        const profile = normalizeUserData(rawData)
        if (profile.status === 'pending' || profile.status === 'suspended') {
          errorMsg.value =
            profile.status === 'suspended'
              ? 'Your account is suspended. Ask a district admin to restore it.'
              : 'Your account is pending district approval. You will be able to sign in once an administrator activates it.'
          await logSignInAttemptForUser(firebaseUser, profile.status)
          currentUser.value = null
          setMonitoringUser(null)
          await signOut(auth)
          return
        }

        // The token must mirror the users doc before rules-gated queries run
        // (schools, students, schoolStats all check the JWT). A fresh Google
        // sign-in has no role claim yet; a role or school change leaves a
        // stale one. One forced refresh fixes both. On an ordinary reload the
        // claims already match and no network call happens — and if the
        // refresh fails we keep the session on the current token rather than
        // treating a network hiccup as a logout.
        if (!claimsMatchUserDoc(claims, rawData)) {
          try {
            await firebaseUser.getIdToken(true)
            accessTokenStale.value = false
          } catch (error) {
            console.warn('Could not refresh the sign-in token; keeping this session:', error)
            // Stay signed in. The banner tells them to reload; a reload retries
            // this refresh. Do not send them to the login page.
            accessTokenStale.value = true
          }
        } else {
          accessTokenStale.value = false
        }

        publishSignedInUser(firebaseUser, profile)
      } finally {
        isLoading.value = false
      }
    })
  }

  // Token refresh interval
  let tokenRefreshInterval = null

  // Set up automatic token refresh every 30 minutes (less aggressive)
  const setupTokenRefresh = () => {
    clearTokenRefresh() // Clear any existing interval

    tokenRefreshInterval = setInterval(
      async () => {
        if (auth.currentUser) {
          try {
            const success = await refreshToken()
            if (!success) {
              console.error('❌ Failed to refresh token - logging user out for security')
              await logout()
            }
          } catch (error) {
            console.error('❌ Token refresh threw an error:', error)
            await logout()
          }
        }
      },
      45 * 60 * 1000
    ) // 45 minutes in milliseconds - refresh BEFORE token expires (Firebase tokens last ~60 minutes)

    // Remove the immediate token refresh test to avoid conflicts
    // setTimeout(async () => {
    //   if (auth.currentUser) {
    //     console.log('🔄 Initial token refresh test...')
    //     await refreshToken()
    //   }
    // }, 5000) // 5 seconds after setup
  }

  // Clear token refresh interval
  const clearTokenRefresh = () => {
    if (tokenRefreshInterval) {
      clearInterval(tokenRefreshInterval)
      tokenRefreshInterval = null
    }
  }

  // Shared post-authentication gate for both popup and redirect sign-in. Confirms
  // the Google account maps to a Firestore users/{uid} doc and is not pending
  // approval, then hydrates currentUser. Returns true when the user may proceed.
  // (onAuthStateChanged runs equivalent logic; keeping it here lets the caller
  // surface a precise error message on the login screen.)
  const finalizeSignedInUser = async (user) => {
    // Same force-refresh as onAuthStateChanged: do this before hydrating
    // currentUser so router.push('/district') does not race a claim-less token.
    await user.getIdToken(true)

    const userDoc = await getDoc(doc(db, 'users', user.uid))
    if (!userDoc.exists()) {
      errorMsg.value = `Your Google account is not registered in ${APP_BRAND.name}. Ask a district or school admin to add you.`
      await logSignInAttemptForUser(user, 'unregistered')
      await signOut(auth)
      currentUser.value = null
      setMonitoringUser(null)
      return false
    }

    const profile = normalizeUserData(userDoc.data())
    if (profile.status === 'pending') {
      errorMsg.value =
        'Your account is pending district approval. You will be able to sign in once an administrator activates it.'
      await logSignInAttemptForUser(user, 'pending')
      await signOut(auth)
      currentUser.value = null
      setMonitoringUser(null)
      return false
    }
    if (profile.status === 'suspended') {
      errorMsg.value = 'Your account is suspended. Ask a district admin to restore it.'
      await logSignInAttemptForUser(user, 'suspended')
      await signOut(auth)
      currentUser.value = null
      setMonitoringUser(null)
      return false
    }

    currentUser.value = {
      uid: user.uid,
      email: user.email,
      name: user.displayName,
      ...profile
    }
    setMonitoringUser(currentUser.value)
    void logSignInAttemptForUser(user, 'admitted')
    return true
  }

  // Navigates away to Google and returns via getRedirectResult() on reload.
  const startRedirectSignIn = async (provider) => {
    try {
      // Preserve any deep-link destination across the full-page redirect, since
      // the app reloads and the ?redirect= query may not survive the round-trip.
      stashPostLoginRedirect(router.currentRoute.value?.query?.[REDIRECT_QUERY_PARAM])
      setRedirectPendingFlag()
      await signInWithRedirect(auth, provider)
    } catch (err) {
      // We never left the page, so clear the flag — otherwise the next load
      // would misreport this as lost-state.
      consumeRedirectPendingFlag()
      console.error('❌ Redirect sign-in failed:', err)
      errorMsg.value = err.message || 'Authentication failed.'
    }
  }

  const loginWithGoogle = async () => {
    errorMsg.value = ''
    const provider = new GoogleAuthProvider()
    // Always show the Google account picker. Staff often have a personal
    // account active in the same browser; without this Google may silently
    // reuse it, and a non-district account fails the users/{uid} lookup.
    provider.setCustomParameters({ prompt: 'select_account' })

    // Phones leave this tab, sign in at Google, and come back — but only when
    // that trip stays on this site (see redirectSignInStaysOnSite). Desktop
    // uses a popup. A blocked popup falls through to the same return path.
    if (
      prefersRedirectSignIn() &&
      typeof window !== 'undefined' &&
      redirectSignInStaysOnSite(auth?.config?.authDomain, window.location.hostname)
    ) {
      await startRedirectSignIn(provider)
      return
    }

    try {
      const result = await signInWithPopup(auth, provider)

      const ok = await finalizeSignedInUser(result.user)
      if (ok) router.push(resolvePostLoginTarget())
    } catch (err) {
      // Browsers that block the popup (extensions, hardened settings, some
      // in-app webviews) fall back to the full-page redirect flow rather than
      // dead-ending on the button.
      const code = err?.code || ''
      if (
        code === 'auth/popup-blocked' ||
        code === 'auth/operation-not-supported-in-this-environment'
      ) {
        await startRedirectSignIn(provider)
        return
      }
      // A user closing the popup themselves is not an error worth showing.
      if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') {
        return
      }
      console.error('❌ Login error:', err)
      errorMsg.value = err.message || 'Authentication failed.'
    }
  }

  const logout = async () => {
    try {
      // Log logout before clearing user data
      if (currentUser.value) {
        await auditLogger.logSystemAccess('logout', {
          userRole: currentUser.value.role,
          success: true
        })
      }

      // Clear the local session first so onAuthStateChanged does not treat this
      // as an unexpected drop and hard-redirect (that path is for expiry).
      currentUser.value = null
      setMonitoringUser(null)
      accessTokenStale.value = false
      await signOut(auth)

      try {
        const { useSchoolStore } = await import('./schoolStore')
        useSchoolStore().reset()
      } catch {
        /* Pinia may be inactive in tests */
      }

      router.push('/login')
    } catch (error) {
      console.error('❌ Logout error:', error)

      // Log logout error
      await auditLogger.logSystemAccess('logout_error', {
        error: error.message,
        success: false
      })
    }
  }

  // Merge Firestore user doc updates into the live session (e.g. role change).
  const mergeCurrentUser = (data) => {
    if (!data || !currentUser.value) return
    currentUser.value = { ...currentUser.value, ...data }
  }

  // Set user manually (for debugging/testing)
  const setUser = (user) => {
    currentUser.value = user
  }

  // Force refresh user token
  const refreshToken = async () => {
    if (auth.currentUser) {
      try {
        await getIdToken(auth.currentUser, true)
        return true
      } catch (error) {
        console.error('❌ Error refreshing token:', error)
        console.error('❌ Error details:', error.code, error.message)
        return false
      }
    }
    return false
  }

  // Check if current token is valid
  const checkTokenValidity = async () => {
    if (!auth.currentUser) {
      return false
    }

    try {
      await getIdToken(auth.currentUser, false) // Don't force refresh, just get current
      return true
    } catch (error) {
      console.error('❌ Current token is invalid:', error)
      return false
    }
  }

  // Computed property for user
  const user = computed(() => currentUser.value)

  // Initialize auth state when store is created
  initAuth()

  return {
    currentUser,
    user,
    errorMsg,
    isLoading,
    accessTokenStale,
    loginWithGoogle,
    logout,
    setUser,
    mergeCurrentUser,
    refreshToken,
    setupTokenRefresh,
    clearTokenRefresh,
    checkTokenValidity
  }
})
